import type { PlanEntitlement } from '@app/features/order/pages/TeamBillingOverview/planEntitlement';
import type { PlanPeriod } from '@app/features/order/pages/TeamBillingOverview/types';
import authBase from '../authBase';

export type PricingVersion = 'v1' | 'v2';

export type PlanOverview = {
    plan: {
        id: number;
        active: boolean;
        invoiced: boolean;
        name: string;
        expirationDate: string;
        seatBased: boolean;
        period: PlanPeriod;
        periodCount: number;
        price: number;
        status: string | null;
        unpaidInvoiceUrl: string | null;
        start: string | null;
        end: string | null;
        daysLeft: number | null;
        canBeModified: boolean;
    };
    nextPlan: {
        name: string;
        seatBased: boolean;
        period: PlanPeriod;
        periodCount: number;
        seatLimit: number;
        scheduledPlanStartDate: string;
    } | null;
    exceededKeyCount: number | null;
    exceededKeyCost: number | null;
    exceededKeyMinutesSinceLastUpdate: number | null;
    disableSubscriptionFunctionality: boolean;
    teamCreditCents: number;
    pricingVersion: PricingVersion;
};

type MetricsStatus = 'OK' | 'NEAR_LIMIT' | 'LIMIT_REACHED' | 'EXCEEDED' | 'UNLIMITED' | 'UPGRADE_REQUIRED' | 'UNKNOWN';

export type MetricInfo = {
    metricCode: PlanEntitlement;
    currentValue: number | null;
    limitValue: number | null;
    metricStatus: MetricsStatus;
};

export type BillingMetrics = {
    data: MetricInfo[];
    meta: {
        count: number;
        retrievedAt: string;
    };
};

export const teamV2Api = authBase
    .enhanceEndpoints({
        addTagTypes: ['teamMemberProjectsV2'],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getPlanOverview: builder.query<PlanOverview, { teamUuid: string }>({
                query: ({ teamUuid }) => ({
                    method: 'GET',
                    url: `/v1/teams/${teamUuid}/plan/overview`,
                }),
            }),
            getBillingMetrics: builder.query<BillingMetrics, { teamUuid: string }>({
                query: ({ teamUuid }) => ({
                    method: 'GET',
                    url: `/v1/teams/${teamUuid}/billing-metrics`,
                }),
            }),
            getPricingVersion: builder.query<{ version: PricingVersion }, { teamUuid: string }>({
                query: ({ teamUuid }) => ({
                    method: 'GET',
                    url: `/v1/teams/${teamUuid}/plan/version`,
                }),
            }),
        }),
    });
